
import { useState, useEffect, useCallback, useRef } from "react";

const getCurrentDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const getFirstDayOfMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
};

const parseDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return { year, month, day };
};


function useReportGrid(fetchFn, mapFn, pageSize = 10,extraParams = {}) {
  const isFirstRender = useRef(true);

  // ── Date state ─────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth);
  const [toDate,   setToDate]   = useState(getCurrentDate);

  const [committedDates, setCommittedDates] = useState({
    from: getFirstDayOfMonth(),
    to:   getCurrentDate(),
  });

  // ── Grid / pagination state ────────────────────────────────────────────────
  const [rowData,         setRowData]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [currentPage,     setCurrentPage]     = useState(0);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalElements,   setTotalElements]   = useState(0);
  const [filteredCount,   setFilteredCount]   = useState(0);
  const [gridApi,         setGridApi]         = useState(null);

  // ── Date handlers ──────────────────────────────────────────────────────────
  const handleFromDateChange = (v) => {
    setFromDate(v);
    if (v > toDate) setToDate(v);
  };

  const handleToDateChange = (v) => {
    if (v < fromDate) return;
    setToDate(v);
  };

  // Debounce: commit dates 800 ms after user stops typing
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(0);
      setCommittedDates({ from: fromDate, to: toDate });
    }, 800);
    return () => clearTimeout(timer);
  }, [fromDate, toDate]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!committedDates.from || !committedDates.to) return;

    setLoading(true);
    setError(null);
    setRowData([]);

    try {
      const result = await fetchFn({
        from:  parseDate(committedDates.from),
        to:    parseDate(committedDates.to),
        page:  currentPage,
        size:  pageSize,
        ...extraParams,
      });
      const mapped = (result.content ?? []).map(mapFn);
      setRowData(mapped);
      setTotalPages(result.totalPages ?? 1);
      setTotalElements(result.totalElements ?? 0);
    } catch (err) {
      console.error("useReportGrid fetch error:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [committedDates, currentPage, fetchFn, mapFn, pageSize,JSON.stringify(extraParams)]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keep filteredCount in sync with totalElements when page changes
  useEffect(() => {
    setFilteredCount(totalElements);
  }, [totalElements, currentPage]);

  // ── AG Grid callbacks ──────────────────────────────────────────────────────
  const onGridReady = (params) => setGridApi(params.api);

  const onFilterChanged = useCallback(
    (params) => {
      if (!params?.api) return;
      const isActive = Object.keys(params.api.getFilterModel()).length > 0;
      setFilteredCount(isActive ? params.api.getDisplayedRowCount() : totalElements);
    },
    [totalElements]
  );

  // ── Pagination ─────────────────────────────────────────────────────────────
  const handlePrevious = () => { if (currentPage > 0) setCurrentPage((p) => p - 1); };
  const handleNext     = () => { if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1); };

  return {
    // date
    fromDate, toDate, handleFromDateChange, handleToDateChange,
    committedDates,
    // data
    rowData, loading, error,
    // pagination
    currentPage, totalPages, totalElements, pageSize,
    handlePrevious, handleNext,
    // grid
    gridApi, filteredCount, onGridReady, onFilterChanged,
  };
}

export default useReportGrid;